import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { httpService } from "../../services/httpService";
import { 
  PencilIcon, 
  TrashIcon, 
  DocumentDuplicateIcon,
  CubeIcon 
} from "@heroicons/react/24/outline";
import DynamicTable from "../../components/common/DynamicTable";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { useProfile } from "../../context/ProfileContext";
import ServiceProcedureDrawer from "./ServiceProcedureDrawer";
import { FaIndianRupeeSign } from "react-icons/fa6";
import ToggleSwitch from "../../components/common/ToggleSwitch"; // Import ToggleSwitch

const MySwal = withReactContent(Swal);

const ServiceProcedure = () => {
  const [procedures, setProcedures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProcedureId, setSelectedProcedureId] = useState(null);
  const { isPlanExpired } = useProfile();

  useEffect(() => {
    fetchProcedures();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) {
      fetchProcedures();
      fetchCategories();
    }
  }, [isDrawerOpen]);

  const fetchProcedures = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-procedures`,
      );
      if (response.code === 200) {
        setProcedures(response?.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load service procedures");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-categories`,
      );
      if (response.code === 200) {
        setCategories(response?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteProcedure = async (id) => {
    try {
      const response = await httpService.delete(
        `/vendorvetclinicservice/delete-service-procedure/${id}`,
      );
      if (response.status) {
        toast.success("Procedure deleted successfully", {
          position: "top-center",
        });
        fetchProcedures();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete procedure");
    }
  };

  const handleDelete = (id) => {
    MySwal.fire({
      text: "Are you sure you want to delete this procedure? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteProcedure(id);
        MySwal.fire({
          title: "Deleted!",
          text: "Procedure has been deleted.",
          icon: "success",
        });
      }
    });
  };

  const handleEdit = (procedure) => {
    setSelectedProcedureId(procedure._id);
    setIsDrawerOpen(true);
  };

  const handleCopy = (procedure) => {
    MySwal.fire({
      text: "Are you sure you want to copy this procedure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, copy it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await httpService.post(
            `/vendorvetclinicservice/copy-service-procedure/${procedure._id}`,
          );
          if (response.status) {
            toast.success("Procedure copied successfully!");
            fetchProcedures();
          }
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to copy procedure");
        }
      }
    });
  };

  // Updated status toggle handler using ToggleSwitch pattern
  const handleStatusToggle = (procedure) => {
    const newStatus = procedure.status === "active" ? "inactive" : "active";
    MySwal.fire({
      text: `Are you sure you want to change the status to ${newStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await httpService.post(
            `/vendorvetclinicservice/change-procedure-status/${procedure._id}`,
            { status: newStatus }
          );
          if (response.status) {
            toast.success("Procedure status updated successfully!");
            fetchProcedures();
          }
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to update status");
        }
      }
    });
  };

  const columns = [
    {
      key: "name",
      header: "Procedure Name",
      sortable: true,
      renderCell: (row) => (
        <div className="py-2" style={{ minWidth: "200px", maxWidth: "350px" }}>
          <p
            className="text-sm font-medium text-gray-900"
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.5",
            }}
            title={row.name || ""}
          >
            {row.name || "-"}
          </p>
          {row.client_friendly_name && (
            <p className="text-xs text-gray-500 mt-1">
              {row.client_friendly_name}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      renderCell: (row) => (
        <span className="text-sm text-gray-700">
          {row.category_id?.name || "-"}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-1">
          <FaIndianRupeeSign className="h-3 w-3 text-gray-600" />
          <span className="text-sm font-medium">
            {Number(row.fixed_price || 0).toFixed(2)}
          </span>
        </div>
      ),
    },
    {
      key: "service_charge",
      header: "Service Charge",
      sortable: true,
      renderCell: (row) => (
        <div>
          {row.has_service_charge ? (
            <span className="text-sm">Yes ({row.tax_percentage || 0}%)</span>
          ) : (
            <span className="text-sm text-gray-400">No</span>
          )}
        </div>
      ),
    },
    {
      key: "products",
      header: "Products",
      sortable: false,
      renderCell: (row) => {
        const count = row.product_ids?.length || 0;
        return (
          <div className="flex items-center gap-1">
            <CubeIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm">{count}</span>
            {count > 0 && (
              <span className="text-xs text-gray-500 ml-1">
                ({count > 1 ? "items" : "item"})
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "barcode",
      header: "Barcode",
      sortable: true,
      renderCell: (row) => (
        <span className="text-xs font-mono">{row.barcode || "-"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: false, // Changed to false since we're using a toggle
      renderCell: (row) => (
        <ToggleSwitch
          isActive={row.status === "active"}
          onChange={() => handleStatusToggle(row)}
          disabled={isPlanExpired}
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Copy",
      icon: (
        <DocumentDuplicateIcon className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
      ),
      onClick: (row) => handleCopy(row),
      colorClasses: "hover:border-blue-500 hover:bg-blue-50",
      shouldShow: () => !isPlanExpired,
    },
    {
      label: "Edit",
      icon: (
        <PencilIcon className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
      ),
      onClick: (row) => handleEdit(row),
      colorClasses: "hover:border-green-500 hover:bg-green-50",
      shouldShow: () => !isPlanExpired,
    },
    {
      label: "Delete",
      icon: (
        <TrashIcon className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
      ),
      onClick: (row) => handleDelete(row._id),
      colorClasses: "hover:border-red-500 hover:bg-red-50",
      shouldShow: () => !isPlanExpired,
    },
  ];

  return (
    <div>
      {loading && <LoaderSpiner text="Loading . . ." />}

      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Service Procedures</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage service procedures with pricing, products, and service charges.
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedProcedureId(null);
            setIsDrawerOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Add New Procedure
        </button>
      </div>

      <DynamicTable
        columns={columns}
        data={procedures}
        actions={actions}
        showPagination={true}
        initialPageSize={10}
        showPageInfo={true}
        searchable={true}
        searchPlaceholder="Search procedures by name, barcode..."
      />

      <ServiceProcedureDrawer
        isOpen={isDrawerOpen}
        procedureId={selectedProcedureId}
        categories={categories}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedProcedureId(null);
        }}
        onSuccess={() => {
          fetchProcedures();
          fetchCategories();
        }}
      />
    </div>
  );
};

export default ServiceProcedure;